import React from "react";
import "./App.css";
import { FontAwesomeLibrary } from "./fontAwesomeLibrary";
import { createBrowserHistory } from "history";
import { Router, Switch, Route } from "react-router-dom";
import { styled, ThemeProvider, useTheme } from "@mui/material/styles";
import theme from "./theme";
import * as Sentry from "@sentry/react";
import { ProtectedRoute } from "components/ProtectedRoute";
import { AuthProvider } from "components/auth/AuthProvider";
import { ToastContainer } from "react-toastify";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import loadable from "@loadable/component";

import { LoadingBackground } from "components/LoadingBackground";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";

FontAwesomeLibrary.init();

const LoadableForms = loadable(() => import("./modules/Forms"), {
  fallback: <LoadingBackground />,
});

const LoadableDirectories = loadable(() => import("./modules/Directories"), {
  fallback: <LoadingBackground />,
});

const LoadablePdfViewer = loadable(() => import("./modules/PdfViewer"), {
  fallback: <LoadingBackground />,
});

const LoadableCorescheduleRedirect = loadable(() => import("./modules/coreschedule"), {
  fallback: <LoadingBackground />,
});

const LoadableEvents = loadable(() => import("./modules/Events"), {
  fallback: <LoadingBackground />,
});

const LoadableSurveys = loadable(() => import("./modules/Surveys"), {
  fallback: <LoadingBackground />,
});

const LoadableUnregistered = loadable(
  () => import("./modules/Forms/Unregistered"),
  {
    fallback: <LoadingBackground />,
  }
);

const LoadableNotFound = loadable(() => import("./modules/NotFound"), {
  fallback: <LoadingBackground />,
});

const LoadableWelcome = loadable(() => import("./modules/Welcome"), {
  fallback: <LoadingBackground />,
});

const LoadableFormDistribution = loadable(
  () => import("./modules/FormDistribution"),
  {
    fallback: <LoadingBackground />,
  }
);

const history = createBrowserHistory();

const {
  REACT_APP_INTERCOM_ID,
  REACT_APP_RELEASE_VERSION,
  REACT_APP_RELEASE_ENVIRONMENT,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENTID,
  REACT_APP_AUTH0_AUDIENCE,
  REACT_APP_SENTRY_DSN,
} = ENV;

Sentry.init({
  dsn: REACT_APP_SENTRY_DSN,
  maxBreadcrumbs: 50,
  release: REACT_APP_RELEASE_VERSION,
  environment: REACT_APP_RELEASE_ENVIRONMENT,
  attachStacktrace: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllInputs: false, // revert back to true
      maskAllText: false,
      mask: [], // remove this to enable mask
      networkDetailAllowUrls: [
        window.location.origin,
        new RegExp(ENV.REACT_APP_API_URL),
        new RegExp(ENV.REACT_APP_SERVICES_API_URL),
      ],
    }),
    Sentry.browserTracingIntegration(),
  ],
  replaysSessionSampleRate: Number(ENV.REACT_APP_SENTRY_REPLAY_SESSION) || 0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 0.1,
  tracePropagationTargets: [new RegExp(ENV.REACT_APP_API_URL)],
});

window.intercomSettings = {
  app_id: REACT_APP_INTERCOM_ID,
  custom_launcher_selector: "#open_intercom",
  hide_default_launcher: true,
};

const Component = () => {
  const theme = useTheme();

  const StyledToastContainer = styled(ToastContainer)`
    .Toastify__toast--warning {
      background-color: ${theme.colors.warn};
    }
    .Toastify__toast--info {
      background-color: ${theme.palette.info.background};
    }
    .Toastify__toast--success {
      background-color: ${theme.palette.positive.main};
      color: white;

      .Toastify__close-button {
        color: white;
      }
    }
    .Toastify__progress-bar--success {
      background-color: ${theme.palette.positive[800]};
    }
    .Toastify__progress-bar--error {
      background-color: ${theme.palette.negative.main};
    }
  `;

  return (
    <>
      <Switch>
        <Route path="/" component={LoadableWelcome} exact />
        <Route path="/events" component={LoadableEvents} />
        <Route path="/survey" component={LoadableSurveys} />
        <Route path="/form" component={LoadableForms} />
        <Route path="/directories" component={LoadableDirectories} />
        <Route path="/pdf-viewer" component={LoadablePdfViewer} />
        <Route path="/coreschedule" component={LoadableCorescheduleRedirect} />
        <Route
          path="/form-list"
          render={() => <ProtectedRoute component={LoadableUnregistered} />}
        />
        <Route path="/form-distribution" component={LoadableFormDistribution} />
        <Route path="*" component={LoadableNotFound} />
      </Switch>
      <StyledToastContainer
        position="bottom-center"
        autoClose={10000}
        closeOnClick={false}
        draggable={false}
        icon={({ type }) => {
          switch (type) {
            case "success":
              return (
                <CheckCircleOutlineOutlinedIcon
                  sx={{ marginRight: "10px", color: "white" }}
                />
              );
            case "warning":
              return (
                <WarningAmberOutlinedIcon
                  sx={{ marginRight: "10px", color: theme.colors.neutral }}
                />
              );
            case "error":
              return (
                <ErrorOutlineOutlinedIcon
                  color={theme.palette.negative.main}
                  sx={{
                    marginRight: "10px",
                    color: theme.palette.negative.main,
                  }}
                />
              );
            default:
              return (
                <InfoOutlinedIcon
                  sx={{ marginRight: "10px", color: theme.palette.info.main }}
                />
              );
          }
        }}
      />
    </>
  );
};

export default () => {
  return (
    <Router history={history}>
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <Component />
          </ThemeProvider>
        </LocalizationProvider>
      </AuthProvider>
    </Router>
  );
};
